import { Component, Signal, computed, inject, input, signal } from "@angular/core";
import { CommonModule, NgClass } from "@angular/common";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatButtonModule } from "@angular/material/button";

import {
  VaultExplorerService,
  VaultItemGroup,
} from "@bitwarden/web-vault/app/services/dali/vault-explorer.service";
import {
  StoreModelNameByVaultCode,
  VaultCodeEnum,
} from "@bitwarden/web-vault/app/services/dali/type/dali.type";
import { StoreData } from "@bitwarden/web-vault/app/models/store/store.data";
import { TransactionStoreModel } from "@bitwarden/web-vault/app/models/store/transaction.store.model";
import { SourceTransactionStoreModel } from "@bitwarden/web-vault/app/models/store/source-transaction.store.model";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { ConfirmationEnum } from "@bitwarden/web-vault/app/models/enum/confirmation.enum";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import {
  VaultCleanupResultDialogComponent,
  CleanupGroupResult,
  KeptSourceEntry,
} from "../../vault-cleanup-result-dialog.component";
import {
  VaultDedupStyleInfoDialogComponent,
  StyleInfoBlock,
} from "../../vault-dedup-style-info-dialog.component";
import { VaultJsonObjectDialogComponent } from "../../vault-json-object-dialog.component";
import { VaultDataTypeBase } from "../vault-data-type-base";
import { GlossScrollDirective } from "@bitwarden/web-vault/app/components/gloss-scroll/gloss-scroll.directive";

export type SrcDedupStyleValue = "A" | "B" | "C" | "ORPHAN" | "SOURCE_ID";

export interface SrcDedupStyleOption {
  value: SrcDedupStyleValue;
  label: string;
  description: string;
  /**
   * Ignored for `"ORPHAN"` and `"SOURCE_ID"` — those modes use dedicated grouping in the component.
   */
  excludeKeys: string[];
}

export const SRC_DEDUP_STYLES: SrcDedupStyleOption[] = [
  {
    value: "A",
    label: "Style-A",
    description: "Literal duplicate, totally safe to delete",
    excludeKeys: ["dc", "dm", "vid", "del"],
  },
  {
    value: "B",
    label: "Style-B",
    description: "One source might be pointing to multiple transactions, not safe to delete",
    excludeKeys: ["dc", "dm", "vid", "del", "srId"],
  },
  {
    value: "C",
    label: "Style-C",
    description: "All sources point to the same transaction, safe to delete",
    excludeKeys: ["dc", "dm", "vid", "del", "id", "_id"],
  },
  {
    value: "ORPHAN",
    label: "Style-Orphan",
    description:
      "Source rows with no linked vault transaction (empty srId, or srId does not match any transaction id)",
    excludeKeys: [],
  },
  {
    value: "SOURCE_ID",
    label: "Style-SourceId",
    description:
      "Same srId means the same linked vault transaction; extra copies can be removed, keeping the best-linked row",
    excludeKeys: [],
  },
];

const SRC_STYLE_INFO: Record<string, StyleInfoBlock> = {
  A: {
    title: "Style-A",
    titleColor: "tw-text-dt-text-action",
    bullets: [
      "This means everything is pretty much the same for all duplicates. It can be deleted safely.",
    ],
  },
  B: {
    title: "Style-B",
    titleColor: "tw-text-orange-600",
    bullets: [
      "This means we are not totally sure if the sources are pointing to the same transaction or not. If you see duplicates here inform the authority, aka Sinan.",
    ],
  },
  C: {
    title: "Style-C",
    titleColor: "tw-text-dt-text-action",
    bullets: [
      "This means all the duplicates are pointing to the same transaction. They might have different ids but for source transactions if they are pointing to the same transaction we just need the latest one assuming the transaction is valid.",
    ],
  },
  ORPHAN: {
    title: "Style-Orphan",
    titleColor: "tw-text-amber-700",
    bullets: [
      "Lists source transactions that do not point at an existing vault transaction: srId is missing/empty, or no transaction row has that id.",
      "Use “Delete orphan source transactions” to remove the rows currently shown (after any description filter).",
    ],
  },
  SOURCE_ID: {
    title: "Style-SourceId",
    titleColor: "tw-text-dt-text-action",
    bullets: [
      "Grouping uses only the source transaction’s srId (the id of the vault transaction it points to).",
      "Rows with the same srId are duplicates for the same link; cleanup keeps the newest row with a valid transaction id, same as other source-transaction styles.",
    ],
  },
};

@Component({
  selector: "app-vault-type-source-transaction",
  standalone: true,
  imports: [GlossScrollDirective, CommonModule, NgClass, MatCheckboxModule, MatButtonModule],
  templateUrl: "./vault-type-source-transaction.component.html",
})
export class VaultTypeSourceTransactionComponent extends VaultDataTypeBase {
  private transactionStore = inject(TransactionStoreService);

  readonly vaultType = input.required<VaultCodeEnum>();
  protected readonly _vaultType: Signal<VaultCodeEnum> = this.vaultType;

  override readonly vaultDuplicatesOnly = signal(true);

  readonly vaultDescriptionSearch = signal("");

  readonly selectedDedupStyle = signal<SrcDedupStyleValue>("A");
  readonly dedupStyles = SRC_DEDUP_STYLES;

  readonly selectedDedupStyleDescription = computed(() => {
    const style = SRC_DEDUP_STYLES.find((s) => s.value === this.selectedDedupStyle());
    return style?.description ?? "";
  });

  readonly isOrphanStyle = computed(() => this.selectedDedupStyle() === "ORPHAN");

  private _groupCache = new Map<SrcDedupStyleValue, VaultItemGroup[]>();
  private _cachedItemsRef: StoreData[] | null = null;

  override readonly vaultItemGroups = computed((): VaultItemGroup[] => {
    const type = this._vaultType();
    if (!type) {
      return [];
    }
    const items = this.vaultExplorer.getItemsByType(type);

    if (items !== this._cachedItemsRef) {
      this._groupCache.clear();
      this._cachedItemsRef = items;
    }

    const style = this.selectedDedupStyle();
    const cached = this._groupCache.get(style);
    if (cached) {
      return cached;
    }

    let groups: VaultItemGroup[];
    if (style === "ORPHAN") {
      const allTransactions = this.vaultExplorer.getCachedItemsByType(VaultCodeEnum.Transaction);
      const transactionIdSet = new Set(
        allTransactions
          .map((t) => String((t as TransactionStoreModel).id ?? "").trim())
          .filter(Boolean),
      );
      const orphans = items.filter((item) => {
        const srId = String((item as SourceTransactionStoreModel).srId ?? "").trim();
        return !srId || !transactionIdSet.has(srId);
      });
      const sorted = [...orphans].sort(
        (a, b) => new Date(b.dc).getTime() - new Date(a.dc).getTime(),
      );
      groups = sorted.map((item) => ({
        contentHash: `orphan:${item.id}`,
        representative: item,
        all: [item],
        count: 1,
      }));
    } else if (style === "SOURCE_ID") {
      groups = this.vaultExplorer.groupItemsBySourceTransactionSrId(items);
    } else {
      const config = SRC_DEDUP_STYLES.find((s) => s.value === style)!;
      groups = this.duplicatesService.getGroupedItemsCustom(type, config.excludeKeys);
    }

    this._groupCache.set(style, groups);
    return groups;
  });

  onDedupStyleChange(ev: Event): void {
    const el = ev.target as HTMLSelectElement;
    this.selectedDedupStyle.set(el.value as SrcDedupStyleValue);
  }

  openDedupStyleInfoDialog(): void {
    this.dialog.open(VaultDedupStyleInfoDialogComponent, {
      width: "min(700px, 95vw)",
      maxHeight: "90vh",
      data: { selectedStyle: this.selectedDedupStyle(), styleInfoMap: SRC_STYLE_INFO },
    });
  }

  openDetails(group: VaultItemGroup): void {
    this.dialog.open(VaultJsonObjectDialogComponent, {
      width: "min(900px, 95vw)",
      maxHeight: "90vh",
      data: { title: "Source transaction", item: group.representative },
    });
  }

  protected override filterGroups(groups: VaultItemGroup[]): VaultItemGroup[] {
    if (this.selectedDedupStyle() === "ORPHAN") {
      if (this.vaultDescriptionSearch().trim() !== "") {
        return groups.filter((g) => this.matchesDescriptionSearch(g.representative));
      }
      return groups;
    }
    groups = super.filterGroups(groups);
    if (this.vaultDescriptionSearch().trim() !== "") {
      groups = groups.filter((g) => this.matchesDescriptionSearch(g.representative));
    }
    return groups;
  }

  onDescriptionSearchInput(ev: Event): void {
    const el = ev.target as HTMLInputElement | null;
    this.vaultDescriptionSearch.set(el?.value ?? "");
  }

  override async removeAllDuplicates(): Promise<void> {
    if (this.selectedDedupStyle() === "ORPHAN") {
      return;
    }
    const type = this._vaultType();
    if (!type || this.vaultDuplicateGroups().length === 0) {
      return;
    }
    const groupCount = this.vaultDuplicateGroups().length;
    const dupRecords = this.vaultTotalDuplicateRecordsToRemove();
    const confirmed = await this.confirmationService.confirmFor(
      ConfirmationEnum.removeAllVaultDuplicatesForType,
      {
        messageReplacement: `Remove ${dupRecords} duplicate record(s) across ${groupCount} group(s)? The latest copy with a valid transaction link is kept.`,
      },
    );
    if (!confirmed) {
      return;
    }

    const style = this.selectedDedupStyle();
    const allDupGroups =
      style === "SOURCE_ID"
        ? this.vaultExplorer
            .groupItemsBySourceTransactionSrId(this.vaultExplorer.getItemsByType(type))
            .filter((g) => g.count > 1)
        : this.duplicatesService
            .getGroupedItemsCustom(
              type,
              SRC_DEDUP_STYLES.find((s) => s.value === style)!.excludeKeys,
            )
            .filter((g) => g.count > 1);

    await this.executeSourceCleanup(type, allDupGroups);
  }

  override async onItemClick(group: VaultItemGroup): Promise<void> {
    if (this.selectedDedupStyle() === "ORPHAN") {
      return;
    }
    if (group.count <= 1) {
      return;
    }
    const type = this._vaultType();
    if (!type) {
      return;
    }

    const confirmed = await this.confirmationService.confirmFor(
      ConfirmationEnum.deletingVaultDuplicates,
      {
        messageReplacement: `This item has ${group.count} copies. Delete duplicate(s) and keep the latest with a valid transaction link?`,
      },
    );
    if (!confirmed) {
      return;
    }

    await this.executeSourceCleanup(type, [group]);
  }

  override async performCleanup(): Promise<void> {
    if (this.selectedDedupStyle() === "ORPHAN") {
      return;
    }
    const type = this._vaultType();
    if (!type) {
      return;
    }
    const allGroups = this.vaultDuplicateGroups();
    if (allGroups.length === 0) {
      return;
    }
    const count = this.vaultCleanupCount() ?? allGroups.length;
    const groupsToClean = allGroups.slice(0, count);

    await this.executeSourceCleanup(type, groupsToClean);
  }

  /**
   * Deletes source-transaction vault rows currently listed under Style-Orphan (respects description filter).
   */
  async deleteOrphanSourceTransactions(): Promise<void> {
    const type = this._vaultType();
    if (!type || this.selectedDedupStyle() !== "ORPHAN") {
      return;
    }
    const groups = this.vaultDisplayedItemGroups();
    if (groups.length === 0) {
      return;
    }
    const toDelete = groups.flatMap((g) => g.all);
    const confirmed = await this.confirmationService.confirmFor(
      ConfirmationEnum.bulkDeleteUnusedVaultSources,
      {
        messageReplacement: `Delete ${toDelete.length} orphan source transaction(s)? These rows do not link to an existing vault transaction (by srId).`,
      },
    );
    if (!confirmed) {
      return;
    }

    try {
      const modelName = StoreModelNameByVaultCode[type];
      await this.vaultExplorer.deleteVaultItems(modelName, toDelete);
      await this.vaultExplorer.reloadVaultDataForType(type);
      this.clearGroupCache();
      this.confirmationService.notify(
        NotificationEnum.success,
        "Orphans removed",
        `${toDelete.length} source transaction(s) deleted.`,
        false,
      );
    } catch {
      this.confirmationService.notify(
        NotificationEnum.error,
        "error",
        "Failed to delete orphan source transactions.",
        false,
      );
    }
  }

  /**
   * Shared cleanup for source transactions.
   *
   * For each group:
   *  1. Pick the latest source tx whose srId matches an existing transaction's id (valid link).
   *  2. If none has a valid srId, delete ALL copies (none point to a real transaction).
   *  3. Show the before/after modal. For kept copies, show the corresponding transaction.
   */
  private async executeSourceCleanup(type: VaultCodeEnum, groups: VaultItemGroup[]): Promise<void> {
    const allTransactions = this.vaultExplorer.getCachedItemsByType(VaultCodeEnum.Transaction);
    const transactionIdSet = new Set(
      allTransactions.map((t) => String(t.id ?? "").trim()).filter(Boolean),
    );

    const results: CleanupGroupResult[] = [];
    const allToDelete: StoreData[] = [];
    const keptEntries: KeptSourceEntry[] = [];

    for (const group of groups) {
      if (group.count <= 1) {
        continue;
      }

      const sorted = [...group.all].sort(
        (a, b) => new Date(b.dc).getTime() - new Date(a.dc).getTime(),
      );

      const bestToKeep = this.pickBestSourceToKeep(sorted, transactionIdSet);

      if (bestToKeep) {
        const deleted = sorted.filter((item) => item !== bestToKeep);
        results.push({ kept: bestToKeep, deleted });
        allToDelete.push(...deleted);

        const srId = String((bestToKeep as SourceTransactionStoreModel).srId ?? "").trim();
        const linkedTx = srId
          ? allTransactions.find((tx) => String(tx.id ?? "").trim() === srId)
          : undefined;

        keptEntries.push({
          source: bestToKeep,
          linkedTransactions: linkedTx ? [linkedTx] : [],
        });
      } else {
        results.push({ kept: sorted[0], deleted: sorted.slice(1), allDeleted: true });
        allToDelete.push(...sorted);
      }
    }

    if (allToDelete.length === 0) {
      return;
    }

    try {
      const modelName = StoreModelNameByVaultCode[type];
      await this.vaultExplorer.deleteVaultItems(modelName, allToDelete);
      await this.vaultExplorer.reloadVaultDataForType(type);
      this.clearGroupCache();
    } catch {
      this.confirmationService.notify(NotificationEnum.error, "error", "Cleanup failed.", false);
      return;
    }

    this.dialog.open(VaultCleanupResultDialogComponent, {
      width: "min(1100px, 95vw)",
      maxHeight: "90vh",
      data: {
        groups: results,
        keptSourceTransactions: keptEntries,
        sourceSectionTitle: "Source & Transaction",
      },
    });
  }

  /**
   * Pick the latest source transaction whose srId matches an existing transaction.
   * Returns null if none has a valid link (all copies are orphaned).
   */
  private pickBestSourceToKeep(
    sortedNewestFirst: StoreData[],
    transactionIdSet: Set<string>,
  ): StoreData | null {
    for (const src of sortedNewestFirst) {
      const srId = String((src as SourceTransactionStoreModel).srId ?? "").trim();
      if (srId && transactionIdSet.has(srId)) {
        return src;
      }
    }
    return null;
  }

  private clearGroupCache(): void {
    this._groupCache.clear();
    this._cachedItemsRef = null;
  }

  private matchesDescriptionSearch(item: StoreData): boolean {
    const q = this.vaultDescriptionSearch().trim().toLowerCase();
    if (!q) {
      return true;
    }
    const dsc = String((item as SourceTransactionStoreModel).dsc ?? "").toLowerCase();
    return dsc.includes(q);
  }
}
